import apiClient from './apiClient';

/**
 * Location service for managing company locations
 *
 * Locations are used for:
 * - Labor law compliance (state determines OT/break rules)
 * - Multi-location scheduling
 * - Employee work location assignment
 */
const locationService = {
  /**
   * Get all locations for the company
   * @param {Object} options - Query options
   * @param {boolean} options.includeInactive - Include inactive locations
   * @param {string} [options.companyId] Optional X-Company-Id for All fan-out
   * @returns {Promise} API response with locations array
   */
  getLocations: async (options = {}) => {
    const params = {};
    if (options.includeInactive) {
      params.includeInactive = 'true';
    }
    const config = { params };
    if (options.companyId) {
      config.headers = { 'X-Company-Id': options.companyId };
    }
    const response = await apiClient.get('/locations', config);
    return response.data;
  },

  /**
   * Get a single location by ID
   * @param {string} locationId - Location UUID
   * @returns {Promise} API response with location data
   */
  getLocation: async (locationId) => {
    const response = await apiClient.get(`/locations/${locationId}`);
    return response.data;
  },

  /**
   * Create a new location
   * @param {Object} locationData - Location information
   * @param {string} locationData.name - Location name (required)
   * @param {string} locationData.description - Description
   * @param {string} locationData.addressLine1 - Street address
   * @param {string} locationData.addressLine2 - Suite/unit
   * @param {string} locationData.city - City
   * @param {string} locationData.state - State code (required for labor law)
   * @param {string} locationData.postalCode - Postal/zip code
   * @param {string} locationData.country - Country (default: US)
   * @param {string} locationData.timezone - Timezone (e.g., 'America/New_York')
   * @param {boolean} locationData.isPrimary - Is this the primary location
   * @returns {Promise} API response with created location
   */
  createLocation: async (locationData) => {
    const response = await apiClient.post('/locations', locationData);
    return response.data;
  },

  /**
   * Update an existing location
   * @param {string} locationId - Location UUID
   * @param {Object} locationData - Updated location information
   * @returns {Promise} API response with updated location
   */
  updateLocation: async (locationId, locationData) => {
    const response = await apiClient.put(`/locations/${locationId}`, locationData);
    return response.data;
  },

  /**
   * Delete (deactivate) a location
   * @param {string} locationId - Location UUID
   * @returns {Promise} API response
   */
  deleteLocation: async (locationId) => {
    const response = await apiClient.delete(`/locations/${locationId}`);
    return response.data;
  },
};

export default locationService;
